<template>
  <v-container fluid class="ebf-page pa-4 pa-md-6">
    <FundPageHeader
      title="Fund Vouchers"
      subtitle="Manual journal and receipt vouchers inside the separate benefit-fund ledger"
      icon="mdi-receipt-text-outline"
    >
      <v-btn color="white" variant="tonal" prepend-icon="mdi-refresh" :loading="loading" @click="loadVouchers(1)">Refresh</v-btn>
      <v-btn v-if="$insertAccess($route.fullPath)" color="amber-lighten-3" prepend-icon="mdi-plus" @click="openCreate">New voucher</v-btn>
    </FundPageHeader>

    <v-card class="ebf-section-card" elevation="0">
      <v-card-text>
        <v-row class="ebf-filter-bar pa-3 mb-4" align="center">
          <v-col cols="12" sm="6" lg="2"><v-select v-model="filters.voucher_type" :items="['JV', 'RV']" label="Voucher type" variant="outlined" density="compact" hide-details clearable  :menu-props="{ contentClass: 'my-custom-dropdown' }"/></v-col>
          <v-col cols="12" sm="6" lg="2"><v-select v-model="filters.fund_type" :items="fundTypes" label="Fund" variant="outlined" density="compact" hide-details clearable  :menu-props="{ contentClass: 'my-custom-dropdown' }"/></v-col>
          <v-col cols="12" sm="6" lg="2"><v-select v-model="filters.status" :items="['POSTED', 'DRAFT', 'CANCELLED']" label="Status" variant="outlined" density="compact" hide-details clearable  :menu-props="{ contentClass: 'my-custom-dropdown' }"/></v-col>
          <v-col cols="12" sm="6" lg="2"><v-text-field v-model="filters.from_date" type="date" label="From date" variant="outlined" density="compact" hide-details /></v-col>
          <v-col cols="12" sm="6" lg="2"><v-text-field v-model="filters.to_date" type="date" label="To date" variant="outlined" density="compact" hide-details /></v-col>
          <v-col cols="12" sm="6" lg="2"><v-btn color="primary" block prepend-icon="mdi-magnify" @click="loadVouchers(1)">Search</v-btn></v-col>
        </v-row>

        <v-data-table class="ebf-table" :headers="headers" :items="vouchers" :loading="loading" :items-per-page="-1" hide-default-footer density="compact" hover>
          <template #item.voucher_date="{ item }">{{ displayDate(item.voucher_date) }}</template>
          <template #item.voucher_type="{ item }"><v-chip size="small" variant="tonal" color="primary">{{ item.voucher_type }}</v-chip></template>
          <template #item.payment_account="{ item }">{{ item.payment_account ? `${item.payment_account.code} - ${item.payment_account.name}` : '-' }}</template>
          <template #item.total_debit="{ item }"><span class="ebf-money">{{ money(item.total_debit) }}</span></template>
          <template #item.total_credit="{ item }"><span class="ebf-money">{{ money(item.total_credit) }}</span></template>
          <template #item.auto_generated="{ item }"><v-chip size="small" :color="item.auto_generated ? 'blue' : 'grey'" variant="tonal">{{ item.auto_generated ? 'Automatic' : 'Manual' }}</v-chip></template>
          <template #item.voucher_status="{ item }"><StatusChip :value="item.voucher_status" /></template>
          <template #item.actions="{ item }"><v-btn icon="mdi-eye-outline" variant="text" color="primary" size="small" @click="viewVoucher(item.id)" /></template>
        </v-data-table>

        <div v-if="pagination.lastPage > 1" class="d-flex justify-center mt-4">
          <v-pagination v-model="pagination.currentPage" :length="pagination.lastPage" density="comfortable" @update:model-value="loadVouchers" />
        </div>
      </v-card-text>
    </v-card>

    <v-dialog v-model="createDialog" max-width="1150" persistent>
      <v-card>
        <v-card-title class="ebf-dialog-title d-flex justify-space-between align-center">
          <span>Post fund voucher</span>
          <v-btn icon="mdi-close" variant="text" color="white" @click="createDialog = false" />
        </v-card-title>
        <v-card-text class="pa-5">
          <v-form ref="formRef">
            <v-row>
              <v-col cols="12" sm="4"><v-select v-model="form.voucher_type" :items="['JV', 'RV']" label="Voucher type" variant="outlined" density="compact" :rules="[required]" @update:model-value="handleVoucherType"  :menu-props="{ contentClass: 'my-custom-dropdown' }"/></v-col>
              <v-col cols="12" sm="4"><v-text-field v-model="form.voucher_date" type="date" label="Voucher date" variant="outlined" density="compact" :rules="[required]" /></v-col>
              <v-col cols="12" sm="4"><v-select v-model="form.fund_type" :items="fundTypes" label="Fund type" variant="outlined" density="compact" :rules="[required]" @update:model-value="handleFundType"  :menu-props="{ contentClass: 'my-custom-dropdown' }"/></v-col>
              <v-col v-if="form.voucher_type === 'RV'" cols="12" md="7">
                <v-autocomplete
                  v-model="form.payment_account_id"
                  :items="paymentAccounts"
                  :item-title="accountTitle"
                  item-value="id"
                  label="Receiving bank/cash account"
                  variant="outlined"
                  density="compact"
                  :rules="[required]"
                  @update:model-value="handlePaymentAccount"
                
                    :menu-props="{ contentClass: 'my-custom-dropdown' }"
/>
              </v-col>
              <v-col v-if="form.voucher_type === 'RV'" cols="12" md="5"><v-text-field v-model="form.payment_method" label="Payment method" variant="outlined" density="compact" readonly /></v-col>
              <v-col cols="12" md="5"><v-text-field v-model="form.reference_no" label="Reference number" variant="outlined" density="compact" /></v-col>
              <v-col cols="12" md="7"><v-text-field v-model="form.remarks" label="Voucher remarks" variant="outlined" density="compact" /></v-col>
            </v-row>

            <v-alert v-if="form.voucher_type === 'RV'" type="info" variant="tonal" density="compact" class="mb-3">
              The selected receiving account must be included as a debit entry. Debit and credit totals must match.
            </v-alert>

            <div class="d-flex justify-space-between align-center mb-2">
              <div class="text-subtitle-1 font-weight-bold">Voucher entries</div>
              <v-btn size="small" variant="tonal" color="primary" prepend-icon="mdi-plus" @click="addEntry">Add line</v-btn>
            </div>
            <div class="overflow-x-auto">
              <v-table density="compact" class="border rounded">
                <thead><tr><th style="min-width:260px">Account</th><th style="min-width:150px">Employee</th><th style="min-width:120px">Direction</th><th style="min-width:120px">Fund</th><th style="min-width:140px">Amount</th><th style="min-width:180px">Remarks</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="(entry, index) in form.entries" :key="entry.key">
                    <td><v-autocomplete v-model="entry.ebf_account_id" :items="accounts" :item-title="accountTitle" item-value="id" density="compact" variant="underlined" hide-details :rules="[required]"  :menu-props="{ contentClass: 'my-custom-dropdown' }"/></td>
                    <td><v-autocomplete v-model="entry.employee_id" :items="employees" :item-title="employeeName" item-value="id" density="compact" variant="underlined" hide-details clearable  :menu-props="{ contentClass: 'my-custom-dropdown' }"/></td>
                    <td><v-select v-model="entry.transaction_type" :items="['DEBIT', 'CREDIT']" density="compact" variant="underlined" hide-details  :menu-props="{ contentClass: 'my-custom-dropdown' }"/></td>
                    <td><v-select v-model="entry.fund_type" :items="['PF', 'GRATUITY']" density="compact" variant="underlined" hide-details :disabled="form.fund_type !== 'MIXED'"  :menu-props="{ contentClass: 'my-custom-dropdown' }"/></td>
                    <td><v-text-field v-model.number="entry.amount" type="number" min="0.01" step="0.01" density="compact" variant="underlined" hide-details /></td>
                    <td><v-text-field v-model="entry.remarks" density="compact" variant="underlined" hide-details /></td>
                    <td><v-btn icon="mdi-delete-outline" color="error" variant="text" size="small" :disabled="form.entries.length <= 2" @click="removeEntry(index)" /></td>
                  </tr>
                </tbody>
              </v-table>
            </div>
            <div class="d-flex justify-end ga-6 mt-3 text-body-2">
              <span>Debit: <strong>{{ money(entryTotals.debit) }}</strong></span>
              <span>Credit: <strong>{{ money(entryTotals.credit) }}</strong></span>
              <span :class="entryTotals.difference === 0 ? 'text-success' : 'text-error'">Difference: <strong>{{ money(entryTotals.difference) }}</strong></span>
            </div>
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4 justify-end">
          <v-btn variant="text" @click="createDialog = false">Cancel</v-btn>
          <v-btn color="primary" prepend-icon="mdi-check-decagram-outline" :loading="saving" @click="saveVoucher">Post voucher</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="detailDialog" max-width="980">
      <v-card>
        <v-card-title class="ebf-dialog-title d-flex justify-space-between align-center"><span>Voucher {{ selectedVoucher?.code }}</span><v-btn icon="mdi-close" variant="text" color="white" @click="detailDialog = false" /></v-card-title>
        <v-card-text class="pa-5">
          <v-row>
            <v-col cols="6" md="3"><div class="ebf-metric-label">Date</div><div>{{ displayDate(selectedVoucher?.voucher_date) }}</div></v-col>
            <v-col cols="6" md="3"><div class="ebf-metric-label">Type</div><div>{{ selectedVoucher?.voucher_type }}</div></v-col>
            <v-col cols="6" md="3"><div class="ebf-metric-label">Fund</div><div>{{ humanize(selectedVoucher?.fund_type) }}</div></v-col>
            <v-col cols="6" md="3"><div class="ebf-metric-label">Status</div><StatusChip :value="selectedVoucher?.voucher_status" /></v-col>
          </v-row>
          <v-divider class="my-4" />
          <v-table density="compact">
            <thead><tr><th>Account</th><th>Employee</th><th>Fund</th><th>Remarks</th><th class="text-right">Debit</th><th class="text-right">Credit</th></tr></thead>
            <tbody>
              <tr v-for="entry in selectedVoucher?.entries || []" :key="entry.id">
                <td>{{ entry.account?.code }} - {{ entry.account?.name }}</td><td>{{ employeeName(entry.employee) }}</td><td>{{ humanize(entry.fund_type) }}</td><td>{{ entry.remarks || '-' }}</td>
                <td class="text-right ebf-money">{{ entry.transaction_type === 'DEBIT' ? money(entry.amount) : '-' }}</td><td class="text-right ebf-money">{{ entry.transaction_type === 'CREDIT' ? money(entry.amount) : '-' }}</td>
              </tr>
            </tbody>
            <tfoot><tr class="font-weight-bold"><td colspan="4">Total</td><td class="text-right">{{ money(selectedVoucher?.total_debit) }}</td><td class="text-right">{{ money(selectedVoucher?.total_credit) }}</td></tr></tfoot>
          </v-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { toast } from "vue3-toastify";
import employeeBenefitFundsApi from "@/services/employeeBenefitFundsApi";
import FundPageHeader from "./components/FundPageHeader.vue";
import StatusChip from "./components/StatusChip.vue";
import { apiError, compactParams, displayDate, employeeName, envelopeData, humanize, money, paginatorMeta, paginatorRows, today } from "./utils";

const loading = ref(false);
const saving = ref(false);
const vouchers = ref([]);
const accounts = ref([]);
const employees = ref([]);
const createDialog = ref(false);
const detailDialog = ref(false);
const selectedVoucher = ref(null);
const formRef = ref(null);
const pagination = reactive({ currentPage: 1, lastPage: 1, total: 0 });
const filters = reactive({ voucher_type: null, fund_type: null, status: null, from_date: null, to_date: null });
const fundTypes = ["PF", "GRATUITY", "MIXED"];
let rowKey = 0;
const blankEntry = (fund = "PF") => ({ key: ++rowKey, ebf_account_id: null, employee_id: null, transaction_type: "DEBIT", amount: null, fund_type: fund === "MIXED" ? "PF" : fund, remarks: "" });
const blankForm = () => ({ voucher_type: "JV", voucher_date: today(), fund_type: "PF", payment_method: "NONE", payment_account_id: null, reference_no: "", remarks: "", entries: [blankEntry("PF"), { ...blankEntry("PF"), transaction_type: "CREDIT" }] });
const form = reactive(blankForm());
const required = (value) => value !== null && value !== undefined && value !== "" || "Required";

const paymentAccounts = computed(() => accounts.value.filter((account) => account.role === "BANK" && account.status));
const entryTotals = computed(() => {
  const debit = form.entries.filter((entry) => entry.transaction_type === "DEBIT").reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  const credit = form.entries.filter((entry) => entry.transaction_type === "CREDIT").reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  return { debit, credit, difference: Math.round((debit - credit) * 100) / 100 };
});
const accountTitle = (account) => `${account.code} - ${account.name}`;

const headers = [
  { title: "Code", key: "code" }, { title: "Date", key: "voucher_date" }, { title: "Type", key: "voucher_type" },
  { title: "Fund", key: "fund_type" }, { title: "Payment account", key: "payment_account" },
  { title: "Debit", key: "total_debit", align: "end" }, { title: "Credit", key: "total_credit", align: "end" },
  { title: "Source", key: "auto_generated", align: "center" }, { title: "Status", key: "voucher_status", align: "center" },
  { title: "Action", key: "actions", sortable: false, align: "center" },
];

const loadSupportData = async () => {
  try {
    const [accountResponse, employeeResponse] = await Promise.all([
      employeeBenefitFundsApi.accounts(), employeeBenefitFundsApi.employees({ status: 1 }),
    ]);
    accounts.value = envelopeData(accountResponse) || [];
    const employeeData = envelopeData(employeeResponse);
    employees.value = paginatorRows(employeeData).length ? paginatorRows(employeeData) : (Array.isArray(employeeData) ? employeeData : []);
  } catch (exception) {
    toast.error(apiError(exception, "Unable to load voucher reference data."));
  }
};

const loadVouchers = async (page = pagination.currentPage) => {
  loading.value = true;
  try {
    const data = envelopeData(await employeeBenefitFundsApi.vouchers({ ...compactParams(filters), page, per_page: 25 }));
    vouchers.value = paginatorRows(data);
    Object.assign(pagination, paginatorMeta(data));
  } catch (exception) {
    toast.error(apiError(exception, "Unable to load fund vouchers."));
  } finally { loading.value = false; }
};

const openCreate = () => {
  Object.assign(form, blankForm());
  createDialog.value = true;
};
const addEntry = () => form.entries.push(blankEntry(form.fund_type));
const removeEntry = (index) => form.entries.splice(index, 1);
const handleVoucherType = (type) => {
  if (type === "JV") { form.payment_account_id = null; form.payment_method = "NONE"; }
};
const handleFundType = (type) => {
  if (type !== "MIXED") form.entries.forEach((entry) => { entry.fund_type = type; });
};
const handlePaymentAccount = (id) => {
  const account = paymentAccounts.value.find((item) => item.id === id);
  form.payment_method = account?.payment_method || "NONE";
  if (id && form.entries.length) {
    form.entries[0].ebf_account_id = id;
    form.entries[0].transaction_type = "DEBIT";
    if (form.fund_type !== "MIXED") form.entries[0].fund_type = form.fund_type;
  }
};

const saveVoucher = async () => {
  const validation = await formRef.value?.validate();
  if (!validation?.valid) return;
  if (form.entries.length < 2 || entryTotals.value.debit <= 0 || Math.abs(entryTotals.value.difference) > 0.009) {
    toast.error("Voucher requires at least two balanced debit and credit entries."); return;
  }
  if (form.entries.some((entry) => !entry.ebf_account_id || Number(entry.amount) <= 0)) {
    toast.error("Select an account and positive amount for every voucher line."); return;
  }
  saving.value = true;
  try {
    await employeeBenefitFundsApi.createVoucher({
      voucher_type: form.voucher_type, voucher_date: form.voucher_date, fund_type: form.fund_type,
      payment_method: form.voucher_type === "RV" ? form.payment_method : "NONE",
      payment_account_id: form.voucher_type === "RV" ? form.payment_account_id : null,
      reference_no: form.reference_no || null, remarks: form.remarks || null,
      entries: form.entries.map(({ key, ...entry }) => ({ ...entry, employee_id: entry.employee_id || null, amount: Number(entry.amount), remarks: entry.remarks || null })),
    });
    toast.success("Fund voucher posted successfully.");
    createDialog.value = false;
    await Promise.all([loadVouchers(1), loadSupportData()]);
  } catch (exception) { toast.error(apiError(exception, "Unable to post the fund voucher.")); }
  finally { saving.value = false; }
};

const viewVoucher = async (id) => {
  detailDialog.value = true; selectedVoucher.value = null;
  try { selectedVoucher.value = envelopeData(await employeeBenefitFundsApi.voucher(id)); }
  catch (exception) { toast.error(apiError(exception, "Unable to load voucher details.")); detailDialog.value = false; }
};

onMounted(() => Promise.all([loadVouchers(1), loadSupportData()]));
</script>
